import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { darkTheme } from '../../lib/tokens';
import { useStepUp } from '../auth/useStepUp';
import { grantQuota } from './api';
import { QuotaGrantModal, type QuotaGrantResult } from './QuotaGrantModal';

// Aktionsleiste über der Trefferliste (CUR-523).
//
// Die ganze Charge geht in EINEM Aufruf raus. Das ist keine Bequemlichkeit:
// `requireStepUp` verbraucht ein Einmal-Token pro Aufruf — fünf Nutzer über
// fünf Aufrufe wären fünfmal 2FA, und genau daran ist der Admin-Weg in CUR-470
// gescheitert, weshalb dort ein Skript neben dem Dashboard entstand.

export function QuotaBulkActionBar({
  selected,
  onDone,
}: {
  selected: Set<string>;
  onDone: () => void;
}): JSX.Element | null {
  const stepUp = useStepUp();
  const [showModal, setShowModal] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: async (result: QuotaGrantResult) => {
      const userIds = Array.from(selected);
      const token = await stepUp.request({
        action: 'user.quotaGrant',
        description: `${result.amount} Nachrichten für ${userIds.length} Nutzer`,
      });
      return grantQuota({ userIds, feature: 'tutor', ...result }, token);
    },
    onSuccess: (data) => {
      setError(null);
      setMessage(`${data.grants.length} Grants vergeben.`);
      onDone();
    },
    onError: (err: Error) => {
      setMessage(null);
      setError(err.message);
    },
  });

  if (selected.size === 0 && !message && !error) return null;

  return (
    <div
      className="mt-4 flex flex-wrap items-center gap-3 rounded-md border px-4 py-3"
      style={{
        background: darkTheme.colors.bgSurface,
        borderColor: darkTheme.colors.borderSubtle,
      }}
    >
      {selected.size > 0 && (
        <>
          <span className="text-sm text-text-primary">
            {selected.size} {selected.size === 1 ? 'Nutzer' : 'Nutzer'} ausgewählt
          </span>
          <button
            type="button"
            onClick={() => setShowModal(true)}
            disabled={mutation.isPending}
            className="rounded-md border px-3 py-1 text-xs text-text-primary disabled:opacity-50"
            style={{ borderColor: darkTheme.colors.borderSubtle }}
          >
            Kontingent vergeben
          </button>
          <button
            type="button"
            onClick={onDone}
            className="text-xs text-text-secondary underline"
          >
            Auswahl aufheben
          </button>
        </>
      )}
      {message && <span className="text-sm text-text-secondary">{message}</span>}
      {error && <span className="text-sm text-red-400">{error}</span>}

      {showModal && (
        <QuotaGrantModal
          userCount={selected.size}
          onCancel={() => setShowModal(false)}
          onSubmit={(result) => {
            setShowModal(false);
            mutation.mutate(result);
          }}
        />
      )}
      {stepUp.modal}
    </div>
  );
}
